import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc, getDoc } from 'firebase/firestore';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { db } from '../../firebase';
import { fetchSupplements } from '../../Utils';
import { findSupplements, searchProductsForSupplement } from '../products/amazonApi';
import config from '../../config';
import { DietStackService } from '../../services/dietStackService';
import { checkStackStaleness, getCurrentUserProfile } from '../../utils/dietStackUtils';
import { extractMedicationIngredients } from '../../api/medications';
import { checkInteractionsForComponent } from '../../utils/interactionChecker';
import { groupInteractionsBySupplementKey } from '../../utils/interactionUtils';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import ProFeature from '../common/ProFeature';
import { color, MAXW } from '../../theme/tokens';
import {
  SYSTEM_STACK_TYPES,
  decideHiddenClears,
  clearSystemStackHidden,
  setSystemStackHidden,
  isSystemStackPinned,
  setSystemStackPinned,
  CLEAR_DECISION,
} from '../../services/systemStackPrefs';

import { resolveBand, RENDER_STATE } from './renderDecision';
import { buildSupplementInteractions } from './interactionMapping';
import useRecommendedStackRefresh from './useRecommendedStackRefresh';
import RecommendedBand from './components/RecommendedBand';
import RecommendedStackCard from './components/RecommendedStackCard';
import MyStacksSection from './components/MyStacksSection';
import UndoToast from './components/UndoToast';
import DeleteStackDialog from './components/DeleteStackDialog';
import ViewStackModal from './components/ViewStackModal';
import {
  GREETING_SUBHEAD_RECOVERY,
  DISCLAIMER,
  STACK_DISPLAY_NAME,
  TOAST,
  INTERACTION_FAILURE_KIND,
  interactionFailureLine,
} from './copy';
import { isMembershipCheckError, LAPSED_BACKEND_ERROR } from '../stack/SafetyNote';

/**
 * The dashboard — one scrolling view, no tabs.
 *
 * `Brand_Design/myStack Dashboard - Engineering Handoff.html` is the build
 * spec, as amended. §1 fixed the order as greeting, Recommended band, My
 * stacks, disclaimer and warned against "simplifying" it; Tanu superseded
 * that on 2026-08-26 after reviewing the preview, because the New stack CTA
 * sat below the fold and new stacks created is the north-star. The order is
 * now greeting, My stacks, Recommended band, disclaimer — fixed at every
 * breakpoint, and unchanged when My stacks is empty. Nothing here reorders on
 * any condition; the only thing that varies is whether the band is present at
 * all, which is §2's call and lives in `renderDecision.js`.
 *
 * Renamed out of the old `V2`-suffixed directory: that suffix named a
 * predecessor deleted in STACK-502, so it had been pointing at nothing for a
 * while (`docs/design-system-compliance/DESIGN_DOC.md` §11).
 *
 * Subscription gating is untouched by this rewrite. Each recommended card is
 * wrapped in the same `ProFeature` with the same feature key it had on the
 * tab this page replaces; no predicate, claim, request or handler changed.
 *
 * That still holds with §2's lapsed-member state (STACK-516). `ProFeature`
 * asks the same question and gets the same answer; all that changed is what it
 * renders when the answer is no and `isLapsed()` is true — a paused card
 * instead of the upgrade prompt. The withholding is presentational and it is
 * done by not passing the data: the paused branch never receives `stack`, so
 * no supplement name, dose or product count reaches the component, let alone
 * the DOM.
 */

const RECOMMENDED_DESCRIPTION = {
  [SYSTEM_STACK_TYPES.MEDICATION]:
    'A proactive stack to help replenish key nutrients your medication may deplete',
  [SYSTEM_STACK_TYPES.DIET]:
    'A proactive stack to address common nutrient gaps from your specific diet',
};

const CONTEXT_LABEL = {
  [SYSTEM_STACK_TYPES.MEDICATION]: 'Current medications',
  [SYSTEM_STACK_TYPES.DIET]: 'Diet preferences',
};

const UPGRADE_COPY = {
  [SYSTEM_STACK_TYPES.MEDICATION]: {
    title: 'Replenish Stacks',
    description:
      'Get personalized supplement recommendations based on your current medications to counteract nutrient depletions.',
  },
  [SYSTEM_STACK_TYPES.DIET]: {
    title: 'Nourish Stacks',
    description:
      'Get personalized supplement recommendations based on your dietary preferences and lifestage.',
  },
};

const PROFILE_ROUTE = {
  [SYSTEM_STACK_TYPES.MEDICATION]: '/profile?section=medications',
  [SYSTEM_STACK_TYPES.DIET]: '/profile?section=basicInfo',
};

/**
 * Where the paused card's Reactivate CTA goes — the same destination the
 * deleted `RecommendedStacksTab` sent a lapsed member to. The reactivation
 * flow itself is out of this ticket's scope; this is a link to it, not a
 * change to it.
 */
const SUBSCRIPTION_ROUTE = '/profile?section=account-subscription';

const formatDateForDisplay = (date) => {
  if (!date || !(date instanceof Date) || date.getTime() === 0) return null;
  try {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return null;
  }
};

const sortStacks = (stacksToSort, sortMethod) => {
  switch (sortMethod) {
    case 'name':
      return [...stacksToSort].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    case 'recent':
    default:
      return [...stacksToSort].sort(
        (a, b) => b.jsUpdatedAt.getTime() - a.jsUpdatedAt.getTime()
      );
  }
};

/**
 * @param {string} userId
 * @param {Object} [userData] - the `users/{uid}` document data, which is where
 *   the pin now lives (STACK-526). Passed in rather than re-read here: this runs
 *   on every dashboard load and on every tick of the diet-regeneration poll, so
 *   a read of its own would be a per-poll cost for a value the caller is already
 *   holding. Omitted, the pin resolves from the legacy field alone — see
 *   `isSystemStackPinned`, which falls back to it.
 */
export async function fetchSystemStacks(userId, userData) {
  try {
    const snapshot = await getDocs(collection(db, 'users', userId, 'system_stacks'));
    if (snapshot.empty) return [];

    const stacks = [];
    snapshot.forEach((snapshotDoc) => {
      const data = snapshotDoc.data();
      if (data && data.stackData) {
        stacks.push({
          id: `system_${snapshotDoc.id}_${userId}`,
          ...data.stackData,
          isSystemGenerated: true,
          systemStackType: snapshotDoc.id,
          // Resolved through the service, not read off this document. The pin
          // moved to `users/{uid}.systemStackPrefs.{type}.pinned` in STACK-526
          // because both generators `.set()` this document with a hardcoded
          // `isPinned: false` on every profile edit, silently unpinning the
          // user. `isSystemStackPinned` still honours the legacy field here as
          // a fallback — there was no migration — and uses this document's
          // `createdAt` to tell "the stack I pinned, rebuilt" from "a different
          // stack, built later".
          isPinned: isSystemStackPinned(userData, snapshotDoc.id, data),
          jsCreatedAt: data.createdAt?.toDate() || null,
          // The band's freshness line reads this. §11 filed the timestamp as
          // outstanding backend work; it has been here all along.
          //
          // Absent means absent — `null`, never `new Date()`. Defaulting to
          // "now" made a stack with no stored timestamp claim it was rebuilt
          // today, in a line whose entire job is telling the user when their
          // recommendation last changed. A wrong freshness date is worse than
          // none: it is the "still looks authoritative" failure this ticket
          // was written about, pointed at the user instead of the code.
          // Every reader here handles null by omitting the date — see
          // `bandFreshness` and the cards' `formatDate`.
          jsUpdatedAt: data.updatedAt?.toDate() || null,
        });
      }
    });
    return stacks;
  } catch (error) {
    console.error('[Dashboard] Error fetching system-generated stacks:', error);
    return [];
  }
}

/**
 * Which kind of interaction-check failure to report — STACK-542.
 *
 * One rule, applied to whatever text the failure carried. The check can fail
 * two ways that look identical in the console and must not look identical to a
 * member: the backend refusing a non-member (`Active subscription required`,
 * mapped by `isMembershipCheckError` — the same predicate and the same string
 * the stack page uses, per STACK-528) and everything else. The ticket requires
 * the kind to come from this rule even on the path where the call is skipped
 * rather than made, which is why the lapsed skip below feeds it
 * `LAPSED_BACKEND_ERROR` instead of naming the kind directly.
 *
 * Takes a string. The `setError` callback is handed `error.message` by
 * `checkInteractionsForComponent`; the outer catch has to unwrap the Error
 * itself.
 */
const interactionFailureKind = (errorText) =>
  isMembershipCheckError(errorText)
    ? INTERACTION_FAILURE_KIND.MEMBERSHIP
    : INTERACTION_FAILURE_KIND.GENERIC;

export default function Dashboard() {
  const navigate = useNavigate();
  const { hasAccess, isLapsed } = useFeatureAccess();

  /**
   * §2's lapsed-member row. Read here and passed down, so the one predicate
   * that decides it has exactly one caller on this page.
   *
   * `isLapsed()` is used as it stands — its answer (history exists, status is
   * neither active nor trialing, grandfathered members excluded) is the one
   * the dashboard already gave before the rewrite, and re-deciding which
   * states count as lapsed is a claims question with its own runbook. While
   * the subscription-history read is still in flight it answers false, so the
   * card shown for that moment is the upgrade prompt rather than the paused
   * card. Both withhold the detail, so the boundary holds either way; only
   * which ask is on screen changes.
   */
  const membershipPaused = isLapsed();

  const [userName, setUserName] = useState('');
  const [userStacks, setUserStacks] = useState([]);
  const [systemStacks, setSystemStacks] = useState([]);
  const [userData, setUserData] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [supplementMap, setSupplementMap] = useState({});
  // key -> product_count, for §3's "{n} products ›" chip. A Map for the same
  // reason SupplementsTable uses one: /supplements/find returns only keys that
  // have products, so absence is meaningful and a zero is not the same as a
  // miss. `findSupplements` swallows its own errors and resolves to an empty
  // list, so a failure here costs the chips and nothing else.
  const [productCounts, setProductCounts] = useState(() => new Map());
  const [loading, setLoading] = useState(true);

  const [shareUrls, setShareUrls] = useState({});
  const [showShareField, setShowShareField] = useState({});
  const [sortBy, setSortBy] = useState('recent');
  const [interactionStatuses, setInteractionStatuses] = useState({});
  const [checkingInteractions, setCheckingInteractions] = useState({});
  const [userMedicationCount, setUserMedicationCount] = useState(0);

  const [toast, setToast] = useState(null);
  const [pendingAction, setPendingAction] = useState(false);
  const [stackToDelete, setStackToDelete] = useState(null);
  const [viewingStack, setViewingStack] = useState(null);

  const [isDietRegenerating, setIsDietRegenerating] = useState(false);
  const dietPollTimerRef = useRef(null);
  const toastSeq = useRef(0);

  const medications = userProfile?.medications;

  /**
   * `userData` for callbacks that must not change identity when it changes.
   * `refreshSystemStacks` is `useRecommendedStackRefresh`'s `onStackUpdated`,
   * and `regenerateDietStack` owns a poll timer; putting `userData` in either
   * dependency array would re-subscribe the hook and restart the poll every
   * time a pin or hidden flag is written. The ref is read at call time, which
   * is when the value is wanted.
   */
  const userDataRef = useRef(null);
  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  const refreshSystemStacks = useCallback(async () => {
    const user = getAuth().currentUser;
    if (!user) return;
    setSystemStacks(await fetchSystemStacks(user.uid, userDataRef.current));
  }, []);

  const stacksByType = useMemo(() => {
    const byType = {};
    for (const stack of systemStacks) {
      if (stack.isSystemGenerated) byType[stack.systemStackType] = stack;
    }
    return byType;
  }, [systemStacks]);

  const isMedicationRefreshing = useRecommendedStackRefresh({
    medications,
    medicationStack: stacksByType[SYSTEM_STACK_TYPES.MEDICATION],
    onStackUpdated: refreshSystemStacks,
    enabled: !loading && hasAccess('medication_support'),
  });

  // ── §2 ────────────────────────────────────────────────────────────────────
  const band = useMemo(
    () =>
      resolveBand({
        userData,
        profile: userProfile,
        stacksByType,
        buildingByType: {
          [SYSTEM_STACK_TYPES.MEDICATION]: isMedicationRefreshing,
          [SYSTEM_STACK_TYPES.DIET]: isDietRegenerating,
        },
      }),
    [userData, userProfile, stacksByType, isMedicationRefreshing, isDietRegenerating]
  );


  /**
   * The Nourish half of the refresh contract. Same reasoning as the medication
   * hook: a diet stack that stops tracking the profile is a silent failure.
   */
  const regenerateDietStack = useCallback(async (userId) => {
    setIsDietRegenerating(true);
    try {
      const profile = await getCurrentUserProfile(userId);
      if (!profile?.diet) {
        setIsDietRegenerating(false);
        return;
      }

      const started = await DietStackService.generateStack(profile);
      if (!started) {
        setIsDietRegenerating(false);
        return;
      }

      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const stacks = await fetchSystemStacks(userId, userDataRef.current);
          const dietStack = stacks.find(
            (stack) => stack.systemStackType === SYSTEM_STACK_TYPES.DIET
          );
          if (dietStack && !(await checkStackStaleness(dietStack))) {
            setSystemStacks(stacks);
            setIsDietRegenerating(false);
            return;
          }
        } catch (error) {
          console.error('[Dashboard] Error polling for diet stack:', error);
          setIsDietRegenerating(false);
          return;
        }

        if (attempts < 20) {
          dietPollTimerRef.current = setTimeout(poll, 1000);
        } else {
          setIsDietRegenerating(false);
        }
      };
      poll();
    } catch (error) {
      console.error('[Dashboard] Error regenerating diet stack:', error);
      setIsDietRegenerating(false);
    }
  }, []);

  // Product counts load on their own, not inside the main load: they feed one
  // chip, and the dashboard must not wait on them or fail with them.
  useEffect(() => {
    if (!config.FEATURES?.AFFILIATE_STORE) return undefined;
    let isMounted = true;

    (async () => {
      const { supplements } = await findSupplements({ sort_by: 'products' });
      if (!isMounted || !Array.isArray(supplements)) return;
      setProductCounts(new Map(supplements.map((s) => [s.key, s.product_count])));
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let isMounted = true;

    async function loadData() {
      setLoading(true);
      const user = getAuth().currentUser;
      if (!user) {
        setLoading(false);
        return;
      }

      // Read in the block below and used two blocks down, where the system
      // stacks resolve their pins. Held in a local rather than read back off
      // `userData`, because `setUserData` has not committed by then — and the
      // pin is now a fact about this document (STACK-526).
      let userDocData = null;

      try {
        const supplementsList = await fetchSupplements(config);
        if (isMounted && Array.isArray(supplementsList)) {
          const map = {};
          supplementsList.forEach((supplement) => {
            map[supplement.key] = supplement.name;
          });
          setSupplementMap(map);
        }

        const [userDoc, medProfileDoc] = await Promise.all([
          getDoc(doc(db, 'users', user.uid)),
          getDoc(doc(db, 'med_profile', user.uid)),
        ]);

        if (userDoc.exists()) userDocData = userDoc.data();

        if (isMounted && userDoc.exists()) {
          const data = userDoc.data();
          // Named `medicationList` rather than `medications`: the component
          // scope already binds that name to the loaded profile's list, and a
          // shadow here reads as the same value when it is the source of it.
          const medicationList = medProfileDoc.exists()
            ? medProfileDoc.data().medications || []
            : [];

          setUserData(data);
          setUserName(data.firstName || user.displayName || user.email);
          setUserMedicationCount(medicationList.length);
          // `diet` rather than `dietPreference`: `systemStackPrefs` and
          // `dietStackUtils` both speak `diet`, and one vocabulary for the
          // profile shape is what keeps the hidden-flag decision and the
          // staleness check from disagreeing about the same user.
          setUserProfile({
            ...data,
            medications: medicationList,
            diet: data.dietPreference || data.diet,
          });

          // The clear rule, evaluated at the one moment the flag matters.
          // STACK-514 deliberately put this at dashboard load rather than
          // scattering clear-writes across the profile-save paths.
          const profile = {
            medications: medicationList,
            diet: data.dietPreference || data.diet,
          };
          const { decision, stackTypes } = decideHiddenClears(data, profile);
          if (decision === CLEAR_DECISION.CLEAR) {
            const cleared = await clearSystemStackHidden(user.uid, stackTypes);
            if (cleared && isMounted) {
              setUserData((previous) => ({
                ...previous,
                // Merged per type, for the reason spelled out in `applyHidden`:
                // `pinned` lives in this object too as of STACK-526, and
                // rebuilding it as `{ hidden: false }` dropped the pin on the
                // very load that resolves it.
                systemStackPrefs: stackTypes.reduce(
                  (prefs, stackType) => ({
                    ...prefs,
                    [stackType]: { ...(prefs[stackType] || {}), hidden: false },
                  }),
                  { ...(previous?.systemStackPrefs || {}) }
                ),
              }));
            }
          }
        }

        const stacksSnapshot = await getDocs(
          query(collection(db, 'stacks'), where('userId', '==', user.uid))
        );
        const stacksData = stacksSnapshot.docs.map((stackDoc) => {
          const data = stackDoc.data();
          const updatedAt = data.updatedAt?.toDate?.() || data.createdAt?.toDate?.() || new Date(0);
          return {
            id: stackDoc.id,
            ...data,
            jsCreatedAt: data.createdAt?.toDate?.() || updatedAt,
            jsUpdatedAt: updatedAt,
          };
        });
        if (isMounted) setUserStacks(sortStacks(stacksData, sortBy));
      } catch (error) {
        console.error('[Dashboard] Error loading dashboard data:', error);
      }

      try {
        let stacks = await fetchSystemStacks(user.uid, userDocData);
        try {
          const userDocForDiet = await getDoc(doc(db, 'users', user.uid));
          const dietPref = userDocForDiet.exists()
            ? userDocForDiet.data()?.dietPreference
            : null;
          stacks = await Promise.all(
            stacks.map((stack) =>
              stack.systemStackType === SYSTEM_STACK_TYPES.DIET
                ? DietStackService.overrideNotesForStack(stack, dietPref, 'v1')
                : stack
            )
          );
        } catch (error) {
          console.warn('[Dashboard] Skipping diet notes override:', error);
        }

        const dietStack = stacks.find(
          (stack) => stack.systemStackType === SYSTEM_STACK_TYPES.DIET
        );
        if (dietStack && (await checkStackStaleness(dietStack))) {
          regenerateDietStack(user.uid);
        }

        if (isMounted) setSystemStacks(stacks);
      } catch (error) {
        console.error('[Dashboard] Error fetching system stacks:', error);
        if (isMounted) setSystemStacks([]);
      }

      if (isMounted) setLoading(false);
    }

    loadData();

    return () => {
      isMounted = false;
      if (dietPollTimerRef.current) clearTimeout(dietPollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Interaction checking (carried over unchanged in substance) ─────────────
  useEffect(() => {
    if (!hasAccess('interaction_checking')) return undefined;
    if (!userProfile) return undefined;

    const bandStacks = Object.values(stacksByType);
    const allStacks = [...userStacks, ...bandStacks];
    if (allStacks.length === 0) return undefined;

    // One per run, aborted by the cleanup (STACK-574). A check can poll for
    // about 104 s; if the stacks or medications change meanwhile, this run's
    // answer is for a list the member no longer has, and the run that replaced
    // it owns the cards from then on. Aborting stops the polling, and the
    // helper then calls none of these callbacks again.
    const controller = new AbortController();
    const { signal } = controller;

    const timer = setTimeout(async () => {
      const user = getAuth().currentUser;
      if (!user) return;

      const stackSupplementMap = {};
      const allKeys = new Set();
      allStacks.forEach((stack) => {
        const keys = (stack.supplements || [])
          .map((supplement) => supplement.supplementId || supplement.key)
          .filter(Boolean);
        stackSupplementMap[stack.id] = keys;
        keys.forEach((key) => allKeys.add(key));
      });

      // A check this run replaced left its cards marked as checking and, being
      // cancelled, does not clear them (below). So a run that decides not to
      // check clears them itself, or they would show checking for good.
      const clearChecking = () => {
        setCheckingInteractions((previous) => {
          const next = { ...previous };
          allStacks.forEach((stack) => {
            next[stack.id] = false;
          });
          return next;
        });
      };

      const medicationIngredients = extractMedicationIngredients(
        userProfile.medications || []
      );
      if (allKeys.size === 0 || medicationIngredients.length === 0) {
        clearChecking();
        setInteractionStatuses((previous) => {
          const next = { ...previous };
          allStacks.forEach((stack) => {
            next[stack.id] = { status: 'VALID', hasInteractions: false };
          });
          return next;
        });
        return;
      }

      /**
       * The third status the dashboard records (STACK-542).
       *
       * It is deliberately NOT `VALID`: `VALID` is the shape `onComplete`
       * writes, and a failed check that borrowed it would be indistinguishable
       * from a completed clean one — the exact defect this ticket exists to
       * fix. `hasInteractions` stays false because we found none, not because
       * there are none, and the cards read `failureKind` rather than the
       * status string so the two surfaces cannot drift over a literal.
       */
      const recordFailure = (failureKind) => {
        setInteractionStatuses((previous) => {
          const next = { ...previous };
          allStacks.forEach((stack) => {
            next[stack.id] = { status: 'FAILED', hasInteractions: false, failureKind };
          });
          return next;
        });
      };

      /**
       * A lapsed member's check is refused by the backend every time, so it is
       * skipped rather than fired — `hasAccess('interaction_checking')` is true
       * for everyone (the key is not in `config.FEATURES.IS_PRO_FEATURE`), which
       * is what sends a doomed request today.
       *
       * The kind still comes from `interactionFailureKind`, fed the very string
       * the backend would have answered with, so the skipped path and the
       * refused path cannot diverge — that is the ticket's requirement, and it
       * means a never-subscribed member (who is not `isLapsed()`) still reaches
       * the same sentence by the ordinary refusal route.
       *
       * It sits *after* the no-medications branch on purpose: `SafetyNote`'s
       * own precedence puts "no prescriptions" ahead of a failure, and a member
       * with nothing to check against is told nothing here, exactly as today.
       */
      if (membershipPaused) {
        clearChecking();
        recordFailure(interactionFailureKind(LAPSED_BACKEND_ERROR));
        return;
      }

      setCheckingInteractions((previous) => {
        const next = { ...previous };
        allStacks.forEach((stack) => {
          next[stack.id] = true;
        });
        return next;
      });

      try {
        const authToken = await user.getIdToken();
        await checkInteractionsForComponent(
          medicationIngredients,
          Array.from(allKeys),
          authToken,
          {
            signal,
            setIsChecking: () => {},
            setError: (error) => {
              // `checkInteractionsForComponent` calls this with `null` first to
              // clear, then with a message if the check rejects. Only the
              // latter is a failure; clearing must not wipe a recorded one.
              if (!error) return;
              console.error('[Dashboard] Interaction check error:', error);
              recordFailure(interactionFailureKind(error));
            },
            onProcessing: () => {
              setInteractionStatuses((previous) => {
                const next = { ...previous };
                allStacks.forEach((stack) => {
                  next[stack.id] = { status: 'PROCESSING', hasInteractions: false };
                });
                return next;
              });
            },
            onComplete: (finalResponse) => {
              const byKey = groupInteractionsBySupplementKey(finalResponse.interactions);
              setInteractionStatuses((previous) => {
                const next = { ...previous };
                allStacks.forEach((stack) => {
                  next[stack.id] = {
                    status: 'VALID',
                    // The shape both card types read, medication names
                    // resolved, built in one place so neither card can be fed
                    // a half-resolved finding (STACK-540).
                    ...buildSupplementInteractions({
                      supplementKeys: stackSupplementMap[stack.id] || [],
                      interactionsByKey: byKey,
                      medications: userProfile.medications || [],
                    }),
                  };
                });
                return next;
              });
            },
          }
        );
      } catch (error) {
        // A cancelled check rejects too, with an AbortError. It did not fail:
        // a newer check replaced it, so it must not put STACK-542's "couldn't
        // check" line on cards the newer check is about to fill.
        if (signal.aborted) return;
        // Reached as well as `setError` — the helper rethrows after reporting —
        // so this is the belt to that braces, and covers a throw from anywhere
        // else in the block. Same rule, applied to the Error's own message.
        console.error('[Dashboard] Error in bulk interaction check:', error);
        recordFailure(interactionFailureKind(error?.message));
      } finally {
        // The newer run owns the spinner; clearing it here would show the
        // cards as done while its check is still running.
        if (!signal.aborted) clearChecking();
      }
    }, 1000);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userStacks, stacksByType, userProfile]);

  // ── §7 toasts ─────────────────────────────────────────────────────────────
  /**
   * One toast at a time (§7). A new action replaces the current one, which is
   * why this takes the whole payload rather than pushing onto a queue.
   *
   * Scroll position is captured here and restored by `handleUndo`: §7 requires
   * Undo to restore "the exact prior state, including scroll position and card
   * order". Card order is stable by construction — pinning does not move a
   * card and hiding only removes one — so the scroll offset is the part that
   * actually needs carrying.
   */
  const fireToast = useCallback((message, undo) => {
    toastSeq.current += 1;
    setToast({
      id: toastSeq.current,
      message,
      onUndo: undo,
      scrollY: typeof window !== 'undefined' ? window.scrollY : 0,
    });
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  const handleUndo = useCallback(async () => {
    const current = toast;
    setToast(null);
    if (!current?.onUndo) return;
    await current.onUndo();
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: current.scrollY, behavior: 'auto' });
    }
  }, [toast]);

  // ── §5 actions ────────────────────────────────────────────────────────────
  const applyPinned = useCallback(
    async (stackType, isPinned) => {
      const user = getAuth().currentUser;
      if (!user) return false;
      const ok = await setSystemStackPinned(user.uid, stackType, isPinned);
      if (ok) {
        // Optimistic, and local: the card must not flicker out and back, and a
        // refetch would re-mount it. §5 — pinning changes a footer, not
        // membership.
        setSystemStacks((previous) =>
          previous.map((stack) =>
            stack.systemStackType === stackType ? { ...stack, isPinned } : stack
          )
        );
        // `userData` is now where the pin actually lives, and it is what the
        // next `fetchSystemStacks` reads. Kept in step for the same reason
        // `applyHidden` does it: a refresh that re-derived the pin from stale
        // `userData` would undo the toggle the user just made.
        setUserData((previous) => {
          const prefs = { ...(previous?.systemStackPrefs?.[stackType] || {}) };
          prefs.pinned = isPinned;
          // Any `pinnedFor` here is dropped rather than carried. The service
          // stamps it from the stack's stored `createdAt`, a Firestore
          // `Timestamp` this component does not hold in that shape, so the
          // honest local value is "no stamp" — and `isSystemStackPinned` fails
          // open on a missing one. The optimistic read therefore agrees with
          // what the next real load resolves, instead of comparing a stale
          // stamp against a fresh `createdAt` and reading as unpinned.
          delete prefs.pinnedFor;
          return {
            ...previous,
            systemStackPrefs: {
              ...(previous?.systemStackPrefs || {}),
              [stackType]: prefs,
            },
          };
        });
      }
      return ok;
    },
    []
  );

  const handlePinToggle = useCallback(
    async (stackType, wasPinned) => {
      setPendingAction(true);
      const ok = await applyPinned(stackType, !wasPinned);
      setPendingAction(false);
      if (!ok) return;

      const name = STACK_DISPLAY_NAME[stackType];
      fireToast(wasPinned ? TOAST.unpinned(name) : TOAST.pinned(name), () =>
        applyPinned(stackType, wasPinned)
      );
    },
    [applyPinned, fireToast]
  );

  const applyHidden = useCallback(async (stackType, hidden) => {
    const user = getAuth().currentUser;
    if (!user) return false;
    const ok = await setSystemStackHidden(user.uid, stackType, hidden);
    if (ok) {
      setUserData((previous) => ({
        ...previous,
        systemStackPrefs: {
          ...(previous?.systemStackPrefs || {}),
          // Merged into the per-type object, not written over it. `pinned` and
          // `pinnedFor` share this object as of STACK-526, and replacing it
          // wholesale dropped them — `fetchSystemStacks` now resolves the pin
          // from here, so hiding a stack would have silently dropped its pin,
          // surfacing when the clear rule unhides it again. The Firestore write
          // is already a merge; this makes the local mirror agree with it.
          [stackType]: { ...(previous?.systemStackPrefs?.[stackType] || {}), hidden },
        },
      }));
    }
    return ok;
  }, []);

  const handleHide = useCallback(
    async (stackType) => {
      setPendingAction(true);
      const ok = await applyHidden(stackType, true);
      setPendingAction(false);
      if (!ok) return;

      fireToast(TOAST.hidden(STACK_DISPLAY_NAME[stackType], stackType), () =>
        applyHidden(stackType, false)
      );
    },
    [applyHidden, fireToast]
  );

  const handleSortChange = useCallback(
    (nextSort) => {
      setSortBy(nextSort);
      setUserStacks((previous) => sortStacks(previous, nextSort));
    },
    []
  );

  const handleTogglePublic = useCallback(async (stackId, currentStatus) => {
    try {
      await updateDoc(doc(db, 'stacks', stackId), { isPublic: !currentStatus });
      setUserStacks((previous) =>
        previous.map((stack) =>
          stack.id === stackId ? { ...stack, isPublic: !currentStatus } : stack
        )
      );
    } catch (error) {
      console.error('[Dashboard] Error updating stack public status:', error);
    }
  }, []);

  const handleShare = useCallback((stackId) => {
    setShareUrls((previous) => ({
      ...previous,
      [stackId]: `${window.location.origin}/view-stack/${stackId}`,
    }));
    setShowShareField((previous) => ({ ...previous, [stackId]: true }));
  }, []);

  // §6: hides the row from view only. The stack stays public and the link
  // stays live — the row returns on next load.
  const handleShareFieldClose = useCallback((stackId) => {
    setShowShareField((previous) => ({ ...previous, [stackId]: false }));
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const stack = stackToDelete;
    setStackToDelete(null);
    if (!stack) return;

    try {
      const stackDoc = await getDoc(doc(db, 'stacks', stack.id));
      if (stackDoc.exists()) {
        const data = stackDoc.data();
        if (data.healthGoalsRef) {
          await deleteDoc(doc(db, 'healthGoals', data.healthGoalsRef));
        }
        if (data.notesRef) await deleteDoc(doc(db, 'notes', data.notesRef));
        await deleteDoc(doc(db, 'stacks', stack.id));
        setUserStacks((previous) => previous.filter((item) => item.id !== stack.id));
      }
    } catch (error) {
      console.error('[Dashboard] Error deleting stack:', error);
    }
    // No toast: §5 makes Delete the one action that is destructive and
    // irreversible, which is why it interrupts beforehand instead.
  }, [stackToDelete]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        width: '100%',
        maxWidth: MAXW, // 1080px, centred — §8
        marginInline: 'auto',
        paddingInline: { xs: '1rem', md: '1.5rem' },
        paddingBlock: { xs: '1.5rem', md: '2rem' },
        display: 'flex',
        flexDirection: 'column',
        // §1's fixed vertical order. Nothing below reorders on any condition.
        gap: { xs: '1.5rem', md: '2rem' },
      }}
    >
      <Box>
        <Box
          component="h1"
          sx={{
            margin: 0,
            fontSize: '1.75rem', // 28px/800/-0.03em
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: color.evergreen,
          }}
        >
          Hi {userName}!
        </Box>
        {/* §9's default subhead ("Curated recommendations based on your
            profile…") was removed on 2026-08-27: with My stacks now above the
            band, a line introducing the recommendations sat above a section
            that no longer came next. Two survive that reasoning, because
            neither is introducing the band: the recovery string, when the band
            is omitted entirely and there is no other way back to it, and the
            lapsed line the 4c render draws, which is about the membership.
            Recovery wins when both apply — a lapsed member who has also hidden
            both stacks needs the way back more than the explanation. */}
        {!band.bandRenders && (
          <Box
            data-testid="greeting-subhead"
            sx={{
              marginTop: '0.5rem',
              maxWidth: '41.25rem', // 660px
              fontSize: '0.875rem',
              color: color.inkSoft,
              lineHeight: 1.6,
            }}
          >
            {GREETING_SUBHEAD_RECOVERY}
          </Box>
        )}
      </Box>

      {/* Order inverted from §1 by Tanu's ruling of 2026-08-26: My stacks
          now precedes the Recommended band at every breakpoint, including
          when My stacks is empty. The reason is a metric, not taste — new
          stacks created is the north-star and the New stack CTA was sitting
          below the fold. §1's hierarchy intent survives, carried by order
          instead of label size: the band keeps its surface, and its eyebrow
          label stays small rather than being promoted to a section heading. */
      }
      <MyStacksSection
        stacks={userStacks}
        supplementMap={supplementMap}
        shareUrls={shareUrls}
        showShareField={showShareField}
        sortBy={sortBy}
        onSortChange={handleSortChange}
        interactionStatuses={interactionStatuses}
        checkingInteractions={checkingInteractions}
        userMedicationCount={userMedicationCount}
        hasInteractionAccess={hasAccess('interaction_checking')}
        onView={setViewingStack}
        onEdit={(stackId) => navigate(`/edit-stack/${stackId}`)}
        onDelete={setStackToDelete}
        onTogglePublic={handleTogglePublic}
        onShare={handleShare}
        onShareFieldClose={handleShareFieldClose}
        onCopyToClipboard={(url) => navigator.clipboard.writeText(url)}
        formatDate={formatDateForDisplay}
        membershipPaused={membershipPaused}
      />

      {/* §2: when both stacks resolve to not-rendered the band is omitted
          entirely — label, freshness line and all. No empty section, no
          placeholder card. */}
      {band.bandRenders && (
        <RecommendedBand
          entries={band.stacks}
          paused={membershipPaused}
          // The predicate `ProFeature` applies to each card below, so the band
          // knows whether any supplement row actually reaches the screen.
          hasStackAccess={(stackType) => hasAccess(stackType)}
          onUpdateProfile={() => navigate('/profile')}
          renderCard={(entry, index) => {
            const { stackType, state, stack } = entry;
            const contextItems =
              stackType === SYSTEM_STACK_TYPES.MEDICATION
                ? (userProfile?.medications || []).map(
                    (medication) => medication.name?.display || 'Unknown medication'
                  )
                : [userProfile?.diet].filter(Boolean);

            const interactions =
              hasAccess('interaction_checking') && stack
                ? interactionStatuses[stack.id]?.supplementInteractions || {}
                : {};
            const decorated = stack
              ? {
                  ...stack,
                  supplements: (stack.supplements || []).map((supplement) => {
                    const key = supplement.supplementId || supplement.key;
                    return interactions[key]
                      ? { ...supplement, interaction: interactions[key] }
                      : supplement;
                  }),
                }
              : stack;

            return (
              <ProFeature
                feature={stackType}
                upgradeTitle={UPGRADE_COPY[stackType].title}
                upgradeDescription={UPGRADE_COPY[stackType].description}
                upgradeSource="dashboard_stack"
                // §2's lapsed row, state 4c. `ProFeature` decides access
                // exactly as before — same predicate, same feature key, same
                // answer; this only supplies what it renders when the answer
                // is no *and* the member has lapsed. `undefined` for everyone
                // else, which is the upgrade prompt this page already showed
                // and the reason a never-subscribed member sees no change.
                fallback={
                  membershipPaused ? (
                    <RecommendedStackCard
                      stackType={stackType}
                      state={RENDER_STATE.PAUSED}
                      description={RECOMMENDED_DESCRIPTION[stackType]}
                      onReactivate={() => navigate(SUBSCRIPTION_ROUTE)}
                    />
                  ) : undefined
                }
              >
                <RecommendedStackCard
                  stackType={stackType}
                  state={state}
                  stack={decorated}
                  description={RECOMMENDED_DESCRIPTION[stackType]}
                  contextLabel={CONTEXT_LABEL[stackType]}
                  contextItems={contextItems}
                  supplementMap={supplementMap}
                  isPending={pendingAction}
                  onManage={() => navigate(PROFILE_ROUTE[stackType])}
                  onUnlock={() => navigate(PROFILE_ROUTE[stackType])}
                  onHide={() => handleHide(stackType)}
                  onPinToggle={() => handlePinToggle(stackType, Boolean(stack?.isPinned))}
                  onSupplementClick={(key) => navigate(`/supplements/${key}`)}
                  // The same line a My stacks card shows when the check could
                  // not run (Tanu, 2026-09-14 — STACK-542). Resolved from the
                  // one helper both card types use, and null unless a failure
                  // kind was actually recorded.
                  interactionFailureLine={
                    hasAccess('interaction_checking') && stack
                      ? interactionFailureLine(interactionStatuses[stack.id]?.failureKind)
                      : null
                  }
                  productCounts={productCounts}
                  // §3 puts the buying path on the card itself, so the chip
                  // opens products in place rather than navigating away —
                  // the same behaviour a user-built stack has (Tanu,
                  // 2026-08-27).
                  fetchProducts={searchProductsForSupplement}
                  // "Never an all-collapsed screen": on mobile the first
                  // recommended card is open and the second is not.
                  defaultExpanded={index === 0}
                />
              </ProFeature>
            );
          }}
        />
      )}

      <Box
        sx={{
          maxWidth: '51.25rem', // 820px
          fontSize: '0.78125rem', // 12.5px
          color: color.inkSoft,
          lineHeight: 1.6,
        }}
      >
        {DISCLAIMER}
      </Box>

      <UndoToast toast={toast} onUndo={handleUndo} onDismiss={dismissToast} />

      <DeleteStackDialog
        open={Boolean(stackToDelete)}
        stackName={stackToDelete?.name || 'this stack'}
        supplementCount={stackToDelete?.supplements?.length || 0}
        onCancel={() => setStackToDelete(null)}
        onConfirm={handleConfirmDelete}
      />

      <ViewStackModal
        open={Boolean(viewingStack)}
        onClose={() => setViewingStack(null)}
        stack={viewingStack}
      />
    </Box>
  );
}
